// SaveOrderBar — fixed bottom bar that surfaces unsaved diary edits.
//
// Now driven by the list of changed-day names (from useDiaryReorder).
//   - 0 days changed → render nothing.
//   - 1 day changed  → "Order changed: Monday" + "Save Order — Monday changed".
//   - N days changed → "Order changed in N days"
//                      + "Save Order — N days changed".

import { CircleAlert, RotateCcw, Save } from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import styles from './SaveOrderBar.module.css';

export interface SaveOrderBarProps {
  /** Day-of-week labels for every day with unsaved local-order edits. */
  changedDays: string[];
  onSave: () => void | Promise<void>;
  onReset: () => void;
  saving?: boolean;
}

export function SaveOrderBar({
  changedDays,
  onSave,
  onReset,
  saving = false,
}: SaveOrderBarProps): JSX.Element | null {
  if (changedDays.length === 0) return null;
  const single = changedDays.length === 1 ? changedDays[0] : null;
  const message = single
    ? `Order changed: ${single}`
    : `Order changed in ${changedDays.length} days`;
  const buttonLabel = single
    ? `Save Order — ${single} changed`
    : `Save Order — ${changedDays.length} days changed`;
  return (
    <div className={styles.bar} role="status" aria-live="polite">
      <span className={styles.message}>
        <CircleAlert size={18} className={styles.icon} aria-hidden />
        {message}
      </span>
      <div className={styles.actions}>
        <Button variant="secondary" icon={RotateCcw} onClick={onReset} disabled={saving}>
          Reset
        </Button>
        <Button
          variant="primary"
          icon={Save}
          onClick={() => void onSave()}
          loading={saving}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
